
import { RevDivider } from '@ds/components/Divider'
import { openModal } from '@ds/components/ModalBase'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import { mapGetters } from 'vuex'

import SwapModal from '@buyback/customer/swap/components/Modal'
import { COUNTRIES, MODAL_NAMES } from '@config/constants'
import { addProductsToCart, addToCart } from '@http/endpoints'
import RatingInfo from '@internal-reviews/components/RatingInfo'
import { InstallmentPaymentPrice } from '@payment'
import { ROUTES } from '@router'
import {
  trackAddToCart,
  trackAddToCartBatch,
  trackErrors,
} from '@tracking/events'

import productTranslations from '../../pages/product.translations'
import Accessories from '../Accessories'
import AddToCart from '../AddToCart'
import AdditionalInfo from '../AdditionalInfo'
import AdvertisedPayment from '../AdvertisedPayment'
import Carriers from '../Carriers'
import EcoBlockAnchor from '../EcoBlockAnchor'
import HighlightedPickers from '../HighlightedPickers'
import MobilePlans from '../MobilePlans'
import Model from '../Model'
import Pickers from '../Pickers'
import PickersWrapper from '../PickersWrapper'
import PriceWrapper from '../PriceWrapper'
import { TYPES } from '../ProductBlock/ProductBlock.constant'
import ReassuranceBlock from '../ReassuranceBlock'
import ReassuranceButtons from '../ReassuranceButtons'
import SellerBlock from '../SellerBlock'
import SellerComment from '../SellerComment'
import SwapBlock, { SWAP_DISABLED, SWAP_ERROR, SWAP_NONE } from '../SwapBlock'
import VatBlock from '../VatBlock'

export default {
  components: {
    Accessories,
    AddToCart,
    AdditionalInfo,
    AdvertisedPayment,
    Carriers,
    EcoBlockAnchor,
    RevDivider,
    HighlightedPickers,
    InstallmentPaymentPrice,
    MobilePlans,
    Model,
    Pickers,
    PickersWrapper,
    PriceWrapper,
    RatingInfo,
    ReassuranceBlock,
    ReassuranceButtons,
    SellerComment,
    SellerBlock,
    SwapModal,
    SwapBlock,
    VatBlock,
  },

  inject: ['openErrorToast'],

  props: {
    product: {
      type: Object,
      required: true,
    },

    productImage: {
      type: Object,
      required: true,
    },

    offers: {
      type: Object,
      required: true,
    },

    pickers: {
      type: Array,
      default: () => [],
    },

    page: {
      type: Object,
      required: true,
    },

    ratings: {
      type: Object,
      required: true,
    },

    modelTitle: {
      type: String,
      required: true,
    },

    modelSubtitles: {
      type: Array,
      required: true,
    },

    swapOffer: {
      type: Object,
      default: () => ({}),
    },

    swapListings: {
      type: Array,
      default: () => [],
    },

    swapStatus: {
      type: String,
      required: true,
    },

    trackingInfo: {
      type: Object,
      default: () => ({}),
    },

    reassurance: {
      type: Object,
      default: () => ({}),
    },

    technicalSpecifications: {
      type: Object,
      required: true,
    },

    shouldDisplayEcoBlock: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      isPrepaidPlanSelected: false,
      swapPayload: {},
      swapRedirection: ROUTES.PRODUCT.HOME,
    }
  },

  computed: {
    ...mapGetters({
      isVatIncluded: 'config/vatIncluded',
      searchRelatedId: 'landingSearch/queryID',
      searchIndex: 'landingSearch/sortDefaultValue',
      country: 'config/country',
      experiments: 'flags/getExperiments',
    }),

    swapOfferPrice() {
      return this.swapOffer.price ?? ''
    },

    productTranslations: () => productTranslations,

    productId() {
      const { uuid } = this.$route.params

      return uuid
    },

    isAccessory() {
      return this.page.type === TYPES.ACCESSORY
    },

    shouldDisplayNewPickers() {
      return this.$config.FF_PRODUCT_PICKERS?.includes(this.country)
    },

    accessories() {
      return !isEmpty(this.technicalSpecifications)
        ? this.technicalSpecifications.comesWith.items ?? []
        : []
    },

    showExcludedAccessoriesModal() {
      return !isEmpty(this.technicalSpecifications)
        ? this.technicalSpecifications.comesWith.displayAccessoryExclusionsModal
        : false
    },

    listingId() {
      return this.offers.activeGrade.id
    },

    price() {
      return this.offers.activeGrade.price
    },

    originalPrice() {
      if (this.shouldDisplayOriginalPrice) {
        return this.product.priceWhenNew
      }

      return {}
    },

    shouldDisplayOriginalPrice() {
      return get(this.offers, 'activeGrade.reassurance.discount.isDisplayed')
    },

    eWaste() {
      return this.product.eWaste
    },

    shipping() {
      return this.offers.activeGrade.reassurance.shipping
    },

    warrantyDelay() {
      return this.offers.activeGrade.reassurance.defaultWarrantyDelay
    },

    discount() {
      return this.offers.activeGrade.reassurance.discount
    },

    seller() {
      return this.offers.activeGrade.merchant
    },

    gradeDescription() {
      return this.offers.activeGrade.paragraphs
    },

    displaySellerBlock() {
      return !this.isAccessory
    },

    hasSwapOffer() {
      return !isEmpty(this.swapOffer)
    },

    shouldDisplaySwap() {
      return this.swapStatus !== SWAP_DISABLED
    },

    category() {
      return (
        this.product.trackingDetails &&
        this.product.trackingDetails.category3Name
      )
    },

    carriers() {
      return this.product.compatibleCarriers
    },

    activeBackboxGrade() {
      return this.offers.activeBackboxGrade
    },

    colors() {
      return get(this.offers, 'colors', [])
    },

    storages() {
      return get(this.offers, 'storages', [])
    },

    grades() {
      return this.offers.grades.map((grade) => {
        return {
          ...grade,
          selected: grade.backboxGrade.value === this.activeBackboxGrade,
        }
      })
    },

    countryIsFR() {
      return this.country === COUNTRIES.FR
    },

    swapModal: () => MODAL_NAMES.BUYBACK.SWAP_PRODUCT,

    swapModalATC: () => MODAL_NAMES.BUYBACK.SWAP_ATC,

    redirectModal: () => MODAL_NAMES.PRODUCT.ADD_TO_CART,

    highlightedPickers() {
      return this.offers.highlightedPickers
    },

    shouldDisplayHighlightedPickers() {
      return this.highlightedPickers.length > 0
    },

    selectedHighlightedPickers() {
      return this.offers.selectedHighlightedPickers
    },

    monthlyPaymentPlan() {
      return this.offers.activeGrade.installmentMonthlyPaymentPlan
    },

    shouldDisplayInstallmentPaymentPrice() {
      return (
        this.monthlyPaymentPlan !== null &&
        this.experiments.splitPaymentWording === 'splitPaymentWordingDisplayed'
      )
    },

    shouldDisplayMobilePlans() {
      return Boolean(this.product.mobilePlan)
    },

    mobilePlanListingId() {
      return this.product.mobilePlan?.listingId
    },

    shouldDisplayCarriers() {
      return !isEmpty(this.carriers)
    },
  },
  methods: {
    async addToCart() {
      const productTrackingInfo = {
        ...this.trackingInfo,
        quantity: 1,
      }

      if (this.isPrepaidPlanSelected) {
        trackAddToCartBatch({
          products: [
            productTrackingInfo,
            {
              name: 'Prepaid plan',
              listingId: this.mobilePlanListingId,
            },
          ],
        })
      } else {
        trackAddToCart({
          product: productTrackingInfo,
        })
      }

      try {
        if (this.isPrepaidPlanSelected) {
          await this.$store.dispatch('http/request', {
            request: addProductsToCart,
            body: {
              listings: [
                {
                  listing_id: this.listingId,
                },
                {
                  listing_id: this.mobilePlanListingId,
                },
              ],
              searchRelatedQuery: {
                id: this.searchRelatedId,
                index: this.searchIndex,
              },
            },
          })
        } else {
          await this.$store.dispatch('http/request', {
            request: addToCart,
            body: {
              listing_id: this.listingId,
              searchRelatedQuery: {
                id: this.searchRelatedId,
                index: this.searchIndex,
              },
            },
          })
        }
      } catch (error) {
        this.openErrorToast()

        trackErrors({
          zone: 'add_to_cart',
          message: error?.payload?.detail ?? 'unknown_error',
        })

        return
      }

      const shouldDisplaySwapModal =
        this.swapStatus === SWAP_NONE || this.swapStatus === SWAP_ERROR

      if (!shouldDisplaySwapModal) {
        openModal(this.redirectModal)

        return
      }

      if (shouldDisplaySwapModal) {
        this.swapRedirection = ROUTES.CHECKOUT.CART
        openModal(this.swapModalATC)

        return
      }

      this.$navigation.push({
        type: 'internal',
        name: ROUTES.CHECKOUT.CART,
      })
    },

    handleContinue() {
      setTimeout(() => {
        openModal(this.redirectModal)
      })
    },

    handlPrepaidPlanChange(isSelected) {
      this.isPrepaidPlanSelected = isSelected
    },
  },
}
